/* eslint-disable react-hooks/exhaustive-deps */
import Axios, {
  AxiosBasicCredentials,
  AxiosInstance,
  AxiosProxyConfig,
  AxiosRequestConfig,
} from 'axios';
import * as React from 'react';
import { HttpErrorModel } from '../models/http-error';
import { log } from '../utils/log';
import { objectFindValueFromSomeKey } from '../utils/object/object-find-value-from-some-key';
import { objectToMap } from '../utils/object/object-to-map';
import { useAuth } from './auth';
import { defaultIfEmpty } from '../utils/string/helpers';

type RequestParamValue = string | number | boolean | undefined;

interface HeaderWithAuthorization {
  Authorization: string;
}

interface HttpPossibleErrorData {
  [key: string]: string | undefined;
  // error?: string;
  // message?: string;
  // details?: string;

  // erro?: string;
  // mensagem?: string;
  // mensagemDesenvolvedor?: string;
  // mensagemUsuario?: string;

  // detail?: string;
  // title?: string;
  // type?: string;
  // instance?: string;

  // parameters?: {...extensions}
  // ...extensions
}

interface HttpPossibleError {
  response?: {
    status?: string | number;
    headers?: { 'content-type': string };
    data?: HttpPossibleErrorData | HttpPossibleErrorData[];
  };
  code?: string | number;
  message?: string;
}

export type RequestParams =
  | { [key: string]: RequestParamValue }
  | Map<string, RequestParamValue>
  | URLSearchParams;

function sanitizeUrl(url: string): string {
  return url.trim().replace(/\/$/, '');
}

function configFactory(config?: HookHttpConfigInterface): AxiosRequestConfig {
  const axiosConfig: AxiosRequestConfig = {};
  if (!!config?.headers) {
    axiosConfig.headers = config.headers;
  }
  if (!!config?.baseUrl) {
    axiosConfig.baseURL = sanitizeUrl(config.baseUrl);
  }
  if (!!config?.withCredentials) {
    axiosConfig.withCredentials = config.withCredentials;
  }
  if (!!config?.authBasicCredentials) {
    axiosConfig.auth = config.authBasicCredentials;
  }
  if (!!config?.proxyConfig) {
    axiosConfig.proxy = config.proxyConfig;
  }
  return axiosConfig;
}

function errorCodeFromStatusCode(statusCode: number): string {
  const errorCodes = {
    400: 'core.bad-request',
    401: 'core.unauthorized',
    403: 'core.forbidden',
    404: 'core.not-found',
    405: 'core.method-not-allowed',
    429: 'core.too-many-requests',
    500: 'core.interna-server-error',
    503: 'core.service-unavailable',
    504: 'core.gateway-timeout',
  };

  return (
    errorCodes?.[statusCode as keyof typeof errorCodes] ??
    'core.unexpected-error'
  );
}

function messageFromStatusCode(statusCode: number): string {
  const messages = {
    400: 'Xii, um erro negocial aconteceu',
    401: 'Xii, sua sessão expirou ou você não está logado',
    403: 'Xii, acesso negado',
    404: 'Xii, a informação não foi encontrada',
    405: 'Xii, problema na requisição',
    429: 'Xii, teve muitas requisições',
    500: 'Xii, ocorreu um erro interno no servidor',
    503: 'Xii, o serviço indisponível',
    504: 'Xii, o tempo de resposta expirou',
  };

  return (
    messages?.[statusCode as keyof typeof messages] ??
    'Xii, um erro inesperado aconteceu'
  );
}

function errorFactory(httpError?: HttpPossibleError): HttpErrorModel {
  const contentType = httpError?.response?.headers?.['content-type'] ?? '';

  let statusCode = +(httpError?.response?.status ?? httpError?.code ?? 0);
  if (isNaN(statusCode)) {
    statusCode = 0;
  }

  const data = httpError?.response?.data ?? [];

  const errorData = Array.isArray(data) ? data : [data];

  const errorKeys = [
    'error',
    'mensagemDesenvolvedor',
    'erro',
    'type',
    'instance',
  ];
  const messageKeys = [
    'detail',
    'title',
    'message',
    'mensagemUsuario',
    'mensagem',
    'details',
  ];

  const errorCode = defaultIfEmpty(
    errorData
      .map((datum) => objectFindValueFromSomeKey(datum, errorKeys))
      .join('|') ?? '',
    errorCodeFromStatusCode(statusCode),
  );
  const message = defaultIfEmpty(
    errorData
      .map((datum) => objectFindValueFromSomeKey(datum, messageKeys))
      .join('\n') ?? '',
    messageFromStatusCode(statusCode),
  );

  const error = new HttpErrorModel(message, statusCode, errorCode);

  const isProblemDetail =
    !Array.isArray(data) && contentType.includes('application/problem+json');

  if (isProblemDetail) {
    const clonedData: { [key: string]: unknown } = structuredClone(data);

    if ('type' in clonedData) clonedData.type = undefined;
    if ('status' in clonedData) clonedData.status = undefined;
    if ('title' in clonedData) clonedData.title = undefined;
    if ('detail' in clonedData) clonedData.detail = undefined;
    if ('instance' in clonedData) clonedData.instance = undefined;
    if ('type' in clonedData) clonedData.type = undefined;

    const extensions = new Map();
    const entriesExtensions = clonedData.parameters
      ? Object.entries(clonedData.parameters)
      : Object.entries(clonedData);

    entriesExtensions
      .filter(([, value]) => value !== undefined)
      .forEach(([key, value]) => extensions.set(key, value));

    if (extensions.size > 0) error.extensions = extensions;
  }

  log('HTTP ⛔ Error', contentType, error.toObject());
  return error;
}

function interceptorRegistry(
  axiosInstance: AxiosInstance,
  token?: string,
): void {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error?: HttpPossibleError) => Promise.reject(errorFactory(error)),
  );

  axiosInstance.interceptors.request.use(
    (config) => {
      if (!!token) {
        (config.headers as HeaderWithAuthorization).Authorization =
          `bearer ${token}`;
      }
      const protectedRequest =
        !!config?.withCredentials ||
        !!config?.auth ||
        !!(config?.headers as HeaderWithAuthorization)?.Authorization;
      log(
        'HTTP%s',
        `${protectedRequest ? ' 🔐' : ''} ${
          config?.method?.toUpperCase?.() ?? ''
        }`,
        config.url,
      );
      return config;
    },
    (error?: HttpPossibleError) => Promise.reject(errorFactory(error)),
  );
}

function requestUrlWithParams(
  url: string,
  params: RequestParams = new Map(),
): string {
  const paramMap = params instanceof Map ? params : objectToMap(params);
  if (paramMap?.size > 0) {
    let paramCounter = url.includes('?') ? 1 : 0;
    paramMap?.forEach((value, key) => {
      if (value !== undefined) {
        url += paramCounter++ > 0 ? '&' : '?';
        url += `${key}=${encodeURIComponent(value.toString())}`;
      }
    });
  }
  return url;
}

function requestGet<Response>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<Response> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    const { data: result } = await axiosInstance.get<Response>(url);
    return result;
  };
}

function requestGetWithHeaders<Data>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<ResponseWithHeaders<Data>> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { headers, data } = await axiosInstance.get<Data>(url);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = { headers, data };
    return result as ResponseWithHeaders<Data>;
  };
}

function requestGetText(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<string> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    const { data: result } = await axiosInstance.get<string>(url, {
      responseType: 'text',
    });
    return result;
  };
}

function requestGetTextWithHeaders(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<ResponseWithHeaders<string>> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { headers, data } = await axiosInstance.get<string>(url, {
      responseType: 'text',
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = { headers, data };
    return result as ResponseWithHeaders<string>;
  };
}

function requestGetBlob(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<Blob> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    const { data: result } = await axiosInstance.get<Blob>(url, {
      responseType: 'blob',
    });
    return result;
  };
}

function requestGetBlobWithHeaders(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<ResponseWithHeaders<Blob>> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { headers, data } = await axiosInstance.get<Blob>(url, {
      responseType: 'blob',
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = { headers, data };
    return result as ResponseWithHeaders<Blob>;
  };
}

function requestDelete<Response>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<Response> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    const { data: result } = await axiosInstance.delete<Response>(url);
    return result;
  };
}

function requestDeleteWithHeaders<Data>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<ResponseWithHeaders<Data>> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { headers, data } = await axiosInstance.delete<Data>(url);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = { headers, data };
    return result as ResponseWithHeaders<Data>;
  };
}

function requestHead<Response>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<Response> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    const { data: result } = await axiosInstance.head<Response>(url);
    return result;
  };
}

function requestHeadWithHeaders<Data>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<ResponseWithHeaders<Data>> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { headers, data } = await axiosInstance.head<Data>(url);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = { headers, data };
    return result as ResponseWithHeaders<Data>;
  };
}

function requestOptions<Response>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<Response> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    const { data: result } = await axiosInstance.options<Response>(url);
    return result;
  };
}

function requestOptionsWithHeaders<Data>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithoutBodyType<ResponseWithHeaders<Data>> {
  return async (url: string, params: RequestParams = new Map()) => {
    url = requestUrlWithParams(url, params);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { headers, data } = await axiosInstance.options<Data>(url);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = { headers, data };
    return result as ResponseWithHeaders<Data>;
  };
}

function requestPost<Response, Payload>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithBodyType<Response, Payload> {
  return async (
    url: string,
    body: Payload = {} as Payload,
    params: RequestParams = new Map(),
  ) => {
    url = requestUrlWithParams(url, params);
    const { data: result } = await axiosInstance.post<Response>(url, body);
    return result;
  };
}

function requestPostWithHeaders<Data, Payload>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithBodyType<ResponseWithHeaders<Data>, Payload> {
  return async (
    url: string,
    body: Payload = {} as Payload,
    params: RequestParams = new Map(),
  ) => {
    url = requestUrlWithParams(url, params);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { headers, data } = await axiosInstance.post<Data>(url, body);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = { headers, data };
    return result as ResponseWithHeaders<Data>;
  };
}

function requestPostBlob<Blob, Payload>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithBodyType<Blob, Payload> {
  return async (
    url: string,
    body: Payload = {} as Payload,
    params: RequestParams = new Map(),
  ) => {
    url = requestUrlWithParams(url, params);
    const { data: result } = await axiosInstance.post<Blob>(url, body, {
      responseType: 'blob',
    });
    return result;
  };
}

function requestPostBlobWithHeaders<Blob, Payload>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithBodyType<ResponseWithHeaders<Blob>, Payload> {
  return async (
    url: string,
    body: Payload = {} as Payload,
    params: RequestParams = new Map(),
  ) => {
    url = requestUrlWithParams(url, params);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { headers, data } = await axiosInstance.post<Blob>(url, body, {
      responseType: 'blob',
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = { headers, data };
    return result as ResponseWithHeaders<Blob>;
  };
}

function requestPut<Response, Payload>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithBodyType<Response, Payload> {
  return async (
    url: string,
    body: Payload = {} as Payload,
    params: RequestParams = new Map(),
  ) => {
    url = requestUrlWithParams(url, params);
    const { data: result } = await axiosInstance.put<Response>(url, body);
    return result;
  };
}

function requestPutWithHeaders<Data, Payload>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithBodyType<ResponseWithHeaders<Data>, Payload> {
  return async (
    url: string,
    body: Payload = {} as Payload,
    params: RequestParams = new Map(),
  ) => {
    url = requestUrlWithParams(url, params);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { headers, data } = await axiosInstance.put<Data>(url, body);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = { headers, data };
    return result as ResponseWithHeaders<Data>;
  };
}

function requestPatch<Response, Payload>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithBodyType<Response, Payload> {
  return async (
    url: string,
    body: Payload = {} as Payload,
    params: RequestParams = new Map(),
  ) => {
    url = requestUrlWithParams(url, params);
    const { data: result } = await axiosInstance.patch<Response>(url, body);
    return result;
  };
}

function requestPatchWithHeaders<Data, Payload>(
  axiosInstance: AxiosInstance,
): HookHttpRequestWithBodyType<ResponseWithHeaders<Data>, Payload> {
  return async (
    url: string,
    body: Payload = {} as Payload,
    params: RequestParams = new Map(),
  ) => {
    url = requestUrlWithParams(url, params);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { headers, data } = await axiosInstance.patch<Data>(url, body);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const result = { headers, data };
    return result as ResponseWithHeaders<Data>;
  };
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type HookHttpHeadersType = { [key: string]: string };
export type HookHttpBaseUrlType = string;
export type HookHttpWithCredentialsType = boolean;
export type HookHttpAuthBasicCredentialsType = AxiosBasicCredentials;
export type HookHttpProxyConfigType = AxiosProxyConfig;
export type HookHttpInjectTokenType = boolean;

export type HookHttpRequestWithoutBodyType<Response> = (
  url: string,
  params: RequestParams,
) => Promise<Response>;

export type HookHttpRequestWithBodyType<Response, Payload> = (
  url: string,
  body?: Payload,
  params?: RequestParams,
) => Promise<Response>;

export interface ResponseWithHeaders<Data> {
  headers: { [key: string]: string };
  data?: Data;
}

export interface HookHttpInterface {
  httpGet<Response>(url: string, params?: RequestParams): Promise<Response>;
  httpGetWithHeaders<Data>(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpGetText(url: string, params?: RequestParams): Promise<string>;
  httpGetTextWithHeaders(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<string>>;
  httpGetBlob(url: string, params?: RequestParams): Promise<Blob>;
  httpGetBlobWithHeaders(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Blob>>;
  httpDelete<Response>(url: string, params?: RequestParams): Promise<Response>;
  httpDeleteWithHeaders<Data>(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpHead<Response>(url: string, params?: RequestParams): Promise<Response>;
  httpHeadWithHeaders<Data>(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpOptions<Response>(url: string, params?: RequestParams): Promise<Response>;
  httpOptionsWithHeaders<Data>(
    url: string,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpPost<Response, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Response>;
  httpPostWithHeaders<Data, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpPostBlob<Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Blob>;
  httpPostBlobWithHeaders<Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Blob>>;
  httpPut<Response, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Response>;
  httpPutWithHeaders<Data, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
  httpPatch<Response, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<Response>;
  httpPatchWithHeaders<Data, Payload>(
    url: string,
    body?: Payload,
    params?: RequestParams,
  ): Promise<ResponseWithHeaders<Data>>;
}

export interface HookHttpConfigInterface {
  headers?: HookHttpHeadersType;
  baseUrl?: HookHttpBaseUrlType;
  withCredentials?: HookHttpWithCredentialsType;
  authBasicCredentials?: HookHttpAuthBasicCredentialsType;
  proxyConfig?: HookHttpProxyConfigType;
  injectToken?: HookHttpInjectTokenType;
}

export function useHttp(config?: HookHttpConfigInterface): HookHttpInterface {
  const axiosConfig = React.useMemo(() => configFactory(config), [config]);

  const axiosInstance = React.useMemo(
    () => Axios.create(axiosConfig),
    [axiosConfig],
  );

  const {
    keycloak: { token },
  } = useAuth();

  React.useEffect(
    () =>
      interceptorRegistry(
        axiosInstance,
        !!config?.injectToken ? token : undefined,
      ),
    [axiosInstance, config?.injectToken, token],
  );

  return {
    httpGet: React.useCallback(requestGet(axiosInstance), []),
    httpGetWithHeaders: React.useCallback(
      requestGetWithHeaders(axiosInstance),
      [],
    ),
    httpGetText: React.useCallback(requestGetText(axiosInstance), []),
    httpGetTextWithHeaders: React.useCallback(
      requestGetTextWithHeaders(axiosInstance),
      [],
    ),
    httpGetBlob: React.useCallback(requestGetBlob(axiosInstance), []),
    httpGetBlobWithHeaders: React.useCallback(
      requestGetBlobWithHeaders(axiosInstance),
      [],
    ),
    httpDelete: React.useCallback(requestDelete(axiosInstance), []),
    httpDeleteWithHeaders: React.useCallback(
      requestDeleteWithHeaders(axiosInstance),
      [],
    ),
    httpHead: React.useCallback(requestHead(axiosInstance), []),
    httpHeadWithHeaders: React.useCallback(
      requestHeadWithHeaders(axiosInstance),
      [],
    ),
    httpOptions: React.useCallback(requestOptions(axiosInstance), []),
    httpOptionsWithHeaders: React.useCallback(
      requestOptionsWithHeaders(axiosInstance),
      [],
    ),
    httpPost: React.useCallback(requestPost(axiosInstance), []),
    httpPostWithHeaders: React.useCallback(
      requestPostWithHeaders(axiosInstance),
      [],
    ),
    httpPostBlob: React.useCallback(requestPostBlob(axiosInstance), []),
    httpPostBlobWithHeaders: React.useCallback(
      requestPostBlobWithHeaders(axiosInstance),
      [],
    ),
    httpPut: React.useCallback(requestPut(axiosInstance), []),
    httpPutWithHeaders: React.useCallback(
      requestPutWithHeaders(axiosInstance),
      [],
    ),
    httpPatch: React.useCallback(requestPatch(axiosInstance), []),
    httpPatchWithHeaders: React.useCallback(
      requestPatchWithHeaders(axiosInstance),
      [],
    ),
  };
}
